import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useContainerLogs } from '@/hooks/useNodes'
import { Button, Card } from '@/admin-design/_common'

interface LogViewerProps {
  containerId: string
  nodeId: number
}

const TAIL_OPTIONS = [100, 500, 1000] as const
// Refresh cadences (ms) — Off disables the poll entirely so we don't
// hammer docker when the user has moved on to another task.
const REFRESH_OPTIONS: Array<{ label: string; ms: number | false }> = [
  { label: 'Off', ms: false },
  { label: '2s', ms: 2000 },
  { label: '5s', ms: 5000 },
  { label: '10s', ms: 10000 },
]

// ANSI SGR (Select Graphic Rendition) → Tailwind class map. Only covers
// the subset slog / most CLI tools emit — everything else gets stripped
// silently so logs stay readable.
const ANSI_FG: Record<number, string> = {
  30: 'text-zinc-500',
  31: 'text-red-400',
  32: 'text-green-400',
  33: 'text-yellow-300',
  34: 'text-sky-400',
  35: 'text-fuchsia-400',
  36: 'text-cyan-300',
  37: 'text-zinc-200',
  90: 'text-zinc-500',
  91: 'text-red-300',
  92: 'text-green-300',
  93: 'text-yellow-200',
  94: 'text-sky-300',
  95: 'text-fuchsia-300',
  96: 'text-cyan-200',
  97: 'text-white',
}

type SgrState = { fg?: string; bold?: boolean; dim?: boolean }

function applySgr(state: SgrState, code: number): SgrState {
  if (code === 0) return {}
  if (code === 1) return { ...state, bold: true }
  if (code === 2) return { ...state, dim: true }
  if (code === 22) return { ...state, bold: false, dim: false }
  if (code === 39) return { ...state, fg: undefined }
  const fg = ANSI_FG[code]
  if (fg) return { ...state, fg }
  return state
}

function classesFor(s: SgrState): string {
  const out: string[] = []
  if (s.fg) out.push(s.fg)
  if (s.bold) out.push('font-semibold')
  if (s.dim) out.push('opacity-60')
  return out.join(' ')
}

// Matches CSI SGR sequences: ESC [ <params> m. Covers the slog color output
// (ESC[90m, ESC[34m, ESC[0m, ESC[36m, ...) the user is seeing raw.
const ANSI_SGR_RE = /\x1b\[([0-9;]*)m/g

function stripAnsi(text: string): string {
  return text.replace(ANSI_SGR_RE, '')
}

// Parse the text once and yield a sequence of span / string chunks colored
// per the ANSI state active at each position. Returning string chunks when
// there is no active style keeps the DOM smaller.
function renderAnsi(text: string): ReactNode[] {
  const out: ReactNode[] = []
  let state: SgrState = {}
  let last = 0
  let key = 0
  ANSI_SGR_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = ANSI_SGR_RE.exec(text)) !== null) {
    if (m.index > last) {
      const chunk = text.slice(last, m.index)
      const cls = classesFor(state)
      out.push(
        cls ? (
          <span key={key++} className={cls}>
            {chunk}
          </span>
        ) : (
          chunk
        ),
      )
    }
    const codes = m[1].split(';').filter((s) => s !== '')
    if (codes.length === 0) {
      state = {}
    } else {
      for (const c of codes) state = applySgr(state, parseInt(c, 10) || 0)
    }
    last = m.index + m[0].length
  }
  if (last < text.length) {
    const chunk = text.slice(last)
    const cls = classesFor(state)
    out.push(
      cls ? (
        <span key={key++} className={cls}>
          {chunk}
        </span>
      ) : (
        chunk
      ),
    )
  }
  return out
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through
  }
  const ta = document.createElement('textarea')
  ta.value = text
  ta.style.position = 'fixed'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  ta.select()
  const ok = document.execCommand('copy')
  document.body.removeChild(ta)
  return ok
}

// The terminal `pre` panel (bg-black text-green-400) is KEPT RAW per the
// CRT/terminal aesthetic — admin-design Card cannot replicate it without
// breaking the visual contract.
export default function LogViewer({ containerId, nodeId }: LogViewerProps) {
  const [tail, setTail] = useState<number>(100)
  const scrollRef = useRef<HTMLPreElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const [refreshMs, setRefreshMs] = useState<number | false>(false)
  const [copied, setCopied] = useState(false)
  // ANSI on = render color escapes as styled spans; off = strip them for
  // a plain monochrome read. Either beats the raw `[90m...[0m` that
  // browsers display when SGR sequences leak through <pre>.
  const [ansi, setAnsi] = useState(true)

  const { data, isLoading, isFetching, refetch } = useContainerLogs(
    containerId,
    nodeId,
    tail,
  )

  // Poll on an interval when live-tail is enabled. Kept local (not inside
  // the react-query hook) so toggling between cadences doesn't cascade into
  // other hooks that share the same query key.
  useEffect(() => {
    if (refreshMs === false) return
    const h = window.setInterval(() => {
      refetch()
    }, refreshMs)
    return () => window.clearInterval(h)
  }, [refreshMs, refetch])

  // Auto-scroll to bottom when logs update
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [data?.logs, autoScroll])

  // Memoize the parsed/stripped log nodes so re-renders (auto-scroll,
  // state toggles) don't re-walk the whole buffer every frame.
  const rendered = useMemo<ReactNode>(() => {
    if (!data?.logs) return null
    // Peel a leading timestamp off every line and render it gray so the
    // eye can chunk log output. Works for both ANSI-on (the rest of the
    // line keeps its SGR colors) and ANSI-off (rest is plain text).
    //
    // Matches:
    //   'Apr 21 01:24:06'         — slog default
    //   '2026-04-21T01:24:06Z'    — ISO 8601 (with/without ms, with/without Z)
    //   '2026-04-21 01:24:06'     — space separator
    const TS = /^(?:\x1b\[[0-9;]*m)*([A-Z][a-z]{2}\s+\d{1,2}\s+\d{2}:\d{2}:\d{2}|\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:[.,]\d+)?Z?)(\s)/
    const lines = data.logs.split('\n')
    const out: ReactNode[] = []
    lines.forEach((line, i) => {
      const m = line.match(TS)
      if (m) {
        const tsEnd = (m[0].length - m[2].length)
        const tsText = m[1]
        const rest = line.slice(tsEnd)
        out.push(
          <span key={i}>
            <span className="text-zinc-500">{tsText}</span>
            {ansi ? renderAnsi(rest) : stripAnsi(rest)}
            {i < lines.length - 1 && '\n'}
          </span>,
        )
      } else {
        out.push(
          <span key={i}>
            {ansi ? renderAnsi(line) : stripAnsi(line)}
            {i < lines.length - 1 && '\n'}
          </span>,
        )
      }
    })
    return out
  }, [data?.logs, ansi])

  const handleScroll = () => {
    if (!scrollRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    // Disable auto-scroll if user scrolls up more than 50px from bottom
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 50)
  }

  return (
    <Card className="overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-border bg-muted/50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">Logs</span>
          {refreshMs !== false && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-success"
              title={`Auto-refreshing every ${refreshMs / 1000}s`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              LIVE
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <span className="text-[10px] text-muted-foreground mr-1">Tail</span>
          {TAIL_OPTIONS.map((n) => (
            <Button
              key={n}
              type="button"
              variant={tail === n ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setTail(n)}
              className="h-auto px-2 py-0.5 text-xs shadow-none"
            >
              {n}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <span className="text-[10px] text-muted-foreground mr-1">Auto</span>
          {REFRESH_OPTIONS.map((o) => (
            <Button
              key={o.label}
              type="button"
              variant={refreshMs === o.ms ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setRefreshMs(o.ms)}
              className="h-auto px-2 py-0.5 text-xs shadow-none"
              title={o.ms === false ? 'Disable auto-refresh' : `Refresh every ${o.label}`}
            >
              {o.label}
            </Button>
          ))}
        </div>

        <Button
          type="button"
          variant={ansi ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setAnsi((v) => !v)}
          className="h-auto px-2 py-0.5 text-xs shadow-none"
          title={ansi ? 'Show raw logs without ANSI colors' : 'Render ANSI colors'}
        >
          ANSI
        </Button>

        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={async () => {
            if (!data?.logs) return
            // Always copy the readable form — raw ANSI bytes paste as
            // `[90m...` in other apps anyway, which is never what the
            // operator actually wants.
            const ok = await copyToClipboard(stripAnsi(data.logs))
            if (ok) {
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1200)
            }
          }}
          disabled={!data?.logs}
          className="h-auto px-2 py-0.5 text-xs shadow-none"
          title="Copy the visible log buffer to clipboard (ANSI stripped)"
        >
          {copied ? 'Copied!' : 'Copy'}
        </Button>

        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => refetch()}
          loading={isFetching}
          className="h-auto px-2 py-0.5 text-xs shadow-none"
        >
          {isFetching ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {/* Log content — terminal aesthetic preserved */}
      <pre
        ref={scrollRef}
        onScroll={handleScroll}
        className="p-4 text-xs font-mono bg-black text-green-400 overflow-auto max-h-[500px] whitespace-pre-wrap break-all border border-gray-700"
      >
        {isLoading && (
          <span className="text-gray-500">Loading logs...</span>
        )}
        {!isLoading && !data?.logs && (
          <span className="text-gray-500">No logs available.</span>
        )}
        {rendered}
      </pre>

      {/* Auto-scroll indicator */}
      {!autoScroll && (
        <div className="px-4 py-1 border-t border-border bg-muted/50 text-center">
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => {
              setAutoScroll(true)
              if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight
              }
            }}
            className="h-auto p-0 text-xs"
          >
            Scroll to bottom
          </Button>
        </div>
      )}
    </Card>
  )
}
